"use client";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { SessionProvider } from "next-auth/react";
import NextTopLoader from "nextjs-toploader";
import React, { useEffect, useState } from "react";
import { Provider } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { store } from "../store/store";
import { NextAppDirEmotionCacheProvider } from "../utils/theme/EmotionCache";
import { ThemeSettings } from "../utils/theme/Theme";
import { EmpresaProvider } from "./(protected)/context/EmpresaContext";
import {
  ErrorMessageProvider,
  useErrorMessage,
} from "./(protected)/context/ErrorMessageContext";
import { ConfigProvider } from "./(protected)/context/ConfigContext";
import CustomizerInitializer from "../components/CustomizerInitializer";

export const MyApp = ({ children }: { children: React.ReactNode }) => {
  const theme = ThemeSettings();

  return (
    <>
      <NextTopLoader color="#49BEFF" />
      <CustomizerInitializer />
      <NextAppDirEmotionCacheProvider options={{ key: "flexy" }}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <EmpresaProvider>
            <ConfigProvider>{children}</ConfigProvider>
          </EmpresaProvider>
        </ThemeProvider>
      </NextAppDirEmotionCacheProvider>
    </>
  );
};

const ErrorMessageHandler = () => {
  const { errorMessage, setErrorMessage } = useErrorMessage();

  useEffect(() => {
    if (errorMessage) {
      toast.error(errorMessage);
      setErrorMessage(null);
    }
  }, [errorMessage, setErrorMessage]);

  return null;
};

export default function RootLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setTimeout(() => setLoading(true), 3000);
  }, []);

  return (
    <SessionProvider basePath="/api/auth">
      <html lang="pt-br" suppressHydrationWarning>
        <body>
          <Provider store={store}>
            <ErrorMessageProvider>
              <ToastContainer limit={1} theme="light" position="top-right" />
              <ErrorMessageHandler />
              {loading ? (
                <MyApp children={children} />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    height: "100vh",
                  }}
                >
                  <CircularProgress color="primary" />
                </Box>
              )}
            </ErrorMessageProvider>
          </Provider>
        </body>
      </html>
    </SessionProvider>
  );
}
